import React, { FC } from "react";
import { useIntl, MessageDescriptor } from "react-intl";
import CloseIcon from "@mui/icons-material/Close";
import Radio from "../../Unknown/Radio";
import FormControlLabel from "../../Unknown/FormControlLabel";
import Typography from "../../Unknown/Typography";
import Box from "../../Unknown/Box";
import IconButton from "../../Unknown/IconButton";
import { PatientInMerge, PatientMergeFieldKey } from "./types";
import messages from "./messages";
import { useRaceTranslations } from "../PatientEditForm/useRaceOptions";
import { useEthnicityTranslations } from "../PatientEditForm/useEthnicityOptions";
import { useSexTranslations } from "../PatientEditForm/useSexOptions";

type FieldDef = { key: PatientMergeFieldKey; label: MessageDescriptor };

// RACE/ETHNICITY/SEX render at Body 1 (16px), the rest at Body 2 (14px) — per Figma.
const FIELD_ROWS: FieldDef[] = [
  { key: "customer", label: messages.fieldCustomer },
  { key: "mrn", label: messages.fieldMrn },
  { key: "firstName", label: messages.fieldFirstName },
  { key: "lastName", label: messages.fieldLastName },
  { key: "dateBirth", label: messages.fieldDateBirth },
  { key: "address", label: messages.fieldAddress },
  { key: "race", label: messages.fieldRace },
  { key: "ethnicity", label: messages.fieldEthnicity },
  { key: "sex", label: messages.fieldSex },
];

const BODY1_FIELDS: PatientMergeFieldKey[] = ["race", "ethnicity", "sex"];

export const ALL_PATIENT_MERGE_FIELD_KEYS: PatientMergeFieldKey[] =
  FIELD_ROWS.map((row) => row.key);

// Header + field rows = grid rows every column must claim (see
// MergePatientsPageContent's gridAutoFlow: "column" layout). Callers that
// render a subset of fields (e.g. the post-customer-merge duplicate-group
// flow, which omits "customer" — the group is already scoped to one
// customer) must pass the same subset here to keep row counts in sync.
export const getPatientMergeGridRowCount = (
  fieldKeys: PatientMergeFieldKey[] = ALL_PATIENT_MERGE_FIELD_KEYS,
): number => 1 + fieldKeys.length;

export const PATIENT_MERGE_GRID_ROW_COUNT = getPatientMergeGridRowCount();

const formatAddress = (patient: PatientInMerge): string | null => {
  const parts = [
    patient.city,
    patient.state,
    patient.postalCode,
    patient.country,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

const getFieldValue = (
  patient: PatientInMerge,
  key: PatientMergeFieldKey,
): string | null => {
  switch (key) {
    case "customer":
      return patient.linkedCustomer ? patient.linkedCustomer.id : null;
    case "mrn":
      return patient.mrn || null;
    case "firstName":
      return patient.firstName || null;
    case "lastName":
      return patient.lastName || null;
    case "dateBirth":
      return patient.dateBirth;
    case "address":
      return formatAddress(patient);
    case "race":
      return patient.race;
    case "ethnicity":
      return patient.ethnicity;
    case "sex":
      return patient.sex;
    default:
      return null;
  }
};

type PatientMergeColumnProps = {
  patient: PatientInMerge;
  fieldSelections: Record<PatientMergeFieldKey, string | null>;
  onFieldSelect: (field: PatientMergeFieldKey, patientId: string) => void;
  showRemove: boolean;
  onRemove: (patientId: string) => void;
  onPatientClick: (mrn: string) => void;
  // Defaults to every field (standalone Patient Merge, C8). The
  // post-customer-merge duplicate-group flow passes a subset that omits
  // "customer" — the group is already scoped to one customer, so that row
  // would be redundant.
  fieldKeys?: PatientMergeFieldKey[];
};

const PatientMergeColumn: FC<PatientMergeColumnProps> = ({
  patient,
  fieldSelections,
  onFieldSelect,
  showRemove,
  onRemove,
  onPatientClick,
  fieldKeys = ALL_PATIENT_MERGE_FIELD_KEYS,
}) => {
  const { formatMessage } = useIntl();
  const fieldRows = FIELD_ROWS.filter((row) => fieldKeys.includes(row.key));

  // Race/ethnicity/sex are stored as DB enum values (e.g. "hispanic",
  // "prefer_not_to_answer") — humanize them for display using the same
  // translation maps PatientEditForm's dropdowns use, so the merge column
  // never shows a raw snake_case value to staff.
  const raceTranslations = useRaceTranslations();
  const ethnicityTranslations = useEthnicityTranslations();
  const sexTranslations = useSexTranslations();

  const getDisplayValue = (
    key: PatientMergeFieldKey,
    value: string | null,
  ): string | null => {
    if (value === null) return null;
    switch (key) {
      case "race":
        return (
          raceTranslations[value as keyof typeof raceTranslations] ?? value
        );
      case "ethnicity":
        return (
          ethnicityTranslations[value as keyof typeof ethnicityTranslations] ??
          value
        );
      case "sex":
        return sexTranslations[value as keyof typeof sexTranslations] ?? value;
      default:
        return value;
    }
  };

  return (
    // Fragment so header + rows become direct grid items of the parent.
    <>
      <Box
        sx={{
          p: "16px",
          borderBottom: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.23)",
          backgroundColor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              variant="h5"
              color="primary"
              onClick={() => onPatientClick(patient.mrn)}
              sx={{ cursor: "pointer" }}
            >
              {patient.lastName}, {patient.firstName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatMessage(messages.appointmentCount, {
                count: patient.appointmentCount,
              })}
            </Typography>
          </Box>
          {showRemove && (
            <IconButton
              size="small"
              aria-label={formatMessage(messages.removePatient)}
              sx={{ color: "error.main", mt: -0.5, mr: -0.5 }}
              onClick={() => onRemove(patient.id)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {fieldRows.map((row, index) => {
        const value = getFieldValue(patient, row.key);
        const isDisabled = value === null;
        const isSelected = fieldSelections[row.key] === patient.id;
        const isBody1 = BODY1_FIELDS.includes(row.key);

        return (
          <Box
            key={row.key}
            sx={{
              pl: "8px",
              pr: "16px",
              py: "16px",
              borderBottom:
                index === fieldRows.length - 1 ? "none" : "1px dashed",
              borderColor: "rgba(0, 0, 0, 0.23)",
              // Always opaque (never the bare translucent selected tint) —
              // the parent grid paints an rgba(0,0,0,0.23) backdrop for the
              // column-divider trick ("cells paint their own white"), so a
              // directly-applied translucent background here would composite
              // against that grey instead of white, rendering muddy grey-blue
              // instead of the intended pale blue. The tint is layered via
              // an inset box-shadow on top of the opaque base instead, so it
              // always composites against white regardless of what's behind
              // this element in the DOM.
              backgroundColor: "background.paper",
              boxShadow: isSelected
                ? "inset 0 0 0 1000px rgba(25, 118, 210, 0.08)"
                : "none",
            }}
          >
            <FormControlLabel
              sx={{ alignItems: "center", width: "100%", m: 0 }}
              control={
                <Radio
                  checked={isSelected}
                  disabled={isDisabled}
                  onChange={() => {
                    if (!isDisabled) onFieldSelect(row.key, patient.id);
                  }}
                />
              }
              label={
                <Box sx={{ ml: 0.5 }}>
                  <Typography
                    variant="caption"
                    color={isDisabled ? "text.disabled" : "text.secondary"}
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      display: "block",
                    }}
                  >
                    {formatMessage(row.label)}
                  </Typography>
                  {row.key === "customer" && patient.linkedCustomer ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {patient.linkedCustomer.name}
                      </Typography>
                      {patient.linkedCustomer.email && (
                        <>
                          <Typography variant="body2">—</Typography>
                          <Typography variant="body2" color="primary">
                            {patient.linkedCustomer.email}
                          </Typography>
                        </>
                      )}
                      {patient.linkedCustomer.phone && (
                        <>
                          <Typography variant="body2">—</Typography>
                          <Typography variant="body2" color="primary">
                            {patient.linkedCustomer.phone}
                          </Typography>
                        </>
                      )}
                    </Box>
                  ) : (
                    <Typography variant={isBody1 ? "body1" : "body2"} noWrap>
                      {getDisplayValue(row.key, value) ?? " "}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Box>
        );
      })}
    </>
  );
};

export default PatientMergeColumn;
